import { type ComponentProps, type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Select } from '@wf/components';

import { type IMinistryDepartmentOffice } from '../../../../../rimm/data/rimmEntityTypes';
import { RimmEntityType } from '../../../../../rimm/data/rimmEntityEnums';
import { fetchRimmEntityByCode } from '../../../../../rimm/services';
import { SearchSelectField } from '../../../../configs';

type TDepartmentOfficeSelectProps = Pick<ComponentProps<typeof Select>, 'defaultValue' | 'onChange'> & {
  departmentOffices: IMinistryDepartmentOffice[] | null;
  setDepartmentOffices: Dispatch<SetStateAction<IMinistryDepartmentOffice[] | null>>;
  ministryCode: string;
  departmentCode: string;
};

function DepartmentOfficeSelect({
  defaultValue,
  onChange,
  departmentOffices,
  setDepartmentOffices,
  ministryCode,
  departmentCode,
}: TDepartmentOfficeSelectProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!departmentOffices) {
      const getDepartmentOfficeOptions = async () => {
        try {
          const {
            data: { ministryDepartments },
          } = await fetchRimmEntityByCode({ entity: RimmEntityType.ministry, code: ministryCode });

          if (ministryDepartments.length) {
            const departmentData = ministryDepartments.find(
              (ministryDepartment) => ministryDepartment.code === departmentCode
            );

            if (departmentData) {
              setDepartmentOffices(departmentData.ministryDepartmentOffices);
            }
          }
        } catch (e) {
          console.error(e);
        }
      };

      void getDepartmentOfficeOptions();
    }
  }, []);

  const departmentOfficeOptions = useMemo(
    () =>
      (departmentOffices ?? []).map(({ officeCode, officeDescription }) => ({
        value: officeCode,
        label: {
          title: officeCode,
          description: officeDescription,
        },
      })),
    [departmentOffices]
  ) as SelectOption[];

  return (
    <Select
      name={SearchSelectField.departmentOfficeCode}
      options={departmentOfficeOptions}
      placeholder={t('Select...')}
      defaultValue={defaultValue}
      onChange={onChange}
      isClearable
    />
  );
}

export default DepartmentOfficeSelect;
